import { subscribeToVehicleSubscription } from '@/graphql/subscriptions'
import {
  Traffic,
  Status,
  Sensor,
  SubscribeToVehicleSubscriptionSubscription,
} from '@/API'
import vehicle from '@/types/vehicle'
import { workStatus } from '@/services/const'
import subscribeToVehicleResponse from '@/types/responses/subscribeToVehicleResponse'
import SubscribeProvider from '@/services/subscribe/providers/SubscribeProvider'
import AppSyncClient from '@/services/query/providers/Connection'

import StatusModel from '@/services/models/Status'
import VehicleWorkStatus from '@/services/vehicle/VehicleWorkStatus'

import SensorAlertNotification from '@/services/models/Sensor/AlertNotification'
import SensorRecoveredNotification from '@/services/models/Sensor/RecoveredNotification'

type VehicleSubscriptionsSubscriptionEvent = {
  data: SubscribeToVehicleSubscriptionSubscription
}

const sensorAlertNotification = new SensorAlertNotification('alert')
const sensorErrorNotification = new SensorAlertNotification('error')
const sensorAlertRecoveredNotification = new SensorRecoveredNotification(
  'alert'
)
const sensorErrorRecoveredNotification = new SensorRecoveredNotification(
  'error'
)

class AppSyncProvider implements SubscribeProvider {
  private getConnection() {
    return AppSyncClient.connection()
  }

  public trafficAction = async (
    traffic: Traffic,
    vehicle: vehicle,
    time: number,
    cb: (
      vehicle: vehicle,
      position: subscribeToVehicleResponse,
      payload: Traffic | Status
    ) => void
  ) => {
    if (!traffic?.sn) return
    traffic.tm = time * 1000

    let latestDrivingTime: number = vehicle.latest_driving_time || traffic.tm
    let status = vehicle.status
    if (status != workStatus.WORK_STATUS_NOTHING) {
      if (traffic.sp > 0) {
        latestDrivingTime = traffic.tm
        if (status == workStatus.WORK_STATUS_STOPPING) {
          status = workStatus.WORK_STATUS_IN_SERVICE
        }
      } else {
        const current_time = new Date(traffic.tm)
        status = StatusModel.investigateVehicleStatus(
          status,
          latestDrivingTime,
          latestDrivingTime,
          current_time
        )
      }
    }
    const { dgn, lat, lon, sp } = traffic
    const delivery_plan_id = vehicle.in_service ? dgn : vehicle.delivery_plan_id
    const latestStatus = StatusModel.convertWorkStatusToVehicleMapStatus(status)
    const data: subscribeToVehicleResponse = {
      delivery_plan_id: delivery_plan_id,
      status: latestStatus,
      position: {
        lat: Number(lat),
        lng: Number(lon),
      },
      speed: sp,
      latest_status_time: vehicle.latest_status_time,
      latest_driving_time: latestDrivingTime,
      in_service: !!vehicle.in_service,
      engine_on: true,
      is_loaded: vehicle.is_loaded,
      is_on_highway: vehicle.is_on_highway,
    }
    cb(vehicle, data, traffic)
  }

  public statusAction = async (
    st: Status,
    vehicle: vehicle,
    time: number,
    cb: (
      vehicle: vehicle,
      position: subscribeToVehicleResponse,
      payload: Traffic | Status
    ) => void
  ) => {
    if (!st?.serial_no) return
    st.time = time * 1000

    let vehicleStatus = StatusModel.convertStatus(<Status>st)
    const isLoaded = AppSyncProvider.isCarLoaded(
      vehicle.is_loaded,
      vehicleStatus
    )
    let isOnHighway = vehicle.is_on_highway
    if (vehicleStatus === workStatus.WORK_STATUS_HIGH_WAY) {
      isOnHighway = true
    } else if (vehicleStatus === workStatus.WORK_STATUS_GENERAL_WAY) {
      isOnHighway = false
    }
    vehicle.is_on_highway = isOnHighway
    let inService = vehicle.in_service
    inService = vehicleStatus != workStatus.WORK_STATUS_RETURNING_WAREHOUSE
    vehicleStatus = StatusModel.convertStatus(<Status>st, inService)
    const latestStatus = StatusModel.convertWorkStatusToVehicleMapStatus(
      vehicleStatus,
      vehicle.status
    )
    const data: subscribeToVehicleResponse = {
      delivery_plan_id: st.delivery_group_no,
      status: <workStatus>latestStatus,
      position: {
        lat: vehicle.position?.lat ?? 0,
        lng: vehicle.position?.lng ?? 0,
      },
      latest_status_time: new Date().getTime(),
      latest_driving_time: st.time,
      in_service: !!inService,
      engine_on: !!vehicle.engine_on,
      is_loaded: isLoaded,
      is_on_highway: isOnHighway,
    }
    cb(vehicle, data, st)
  }

  public sensorAction = async (
    sensor: Sensor,
    time: number,
    vehicle: vehicle
  ) => {
    if (!sensor?.sn) {
      return
    }
    sensor.tm = time

    sensorAlertRecoveredNotification.notify(vehicle, sensor)
    sensorErrorRecoveredNotification.notify(vehicle, sensor)
    sensorAlertNotification.notify(vehicle, sensor)
    sensorErrorNotification.notify(vehicle, sensor)
  }

  public switchAlertNotification(flag: boolean) {
    sensorAlertRecoveredNotification.setNotificationable(flag)
    sensorErrorRecoveredNotification.setNotificationable(flag)
    sensorAlertNotification.setNotificationable(flag)
    sensorErrorNotification.setNotificationable(flag)
  }

  private static isCarLoaded(
    currentIsLoadedValue: boolean,
    status: workStatus
  ): boolean {
    const vehicleWorkStatusEntity = new VehicleWorkStatus(status)
    const needToChangeValue =
      vehicleWorkStatusEntity.isEventRelatedToCarLoadEvent()
    if (needToChangeValue) {
      return vehicleWorkStatusEntity.isCarLoaded()
    }
    return currentIsLoadedValue
  }

  public subscribeToVehicleSubscriptions = (
    vehicle: vehicle,
    trafficAction: (
      vehicle: vehicle,
      position: subscribeToVehicleResponse,
      payload: Traffic | Status
    ) => void,
    statusAction: (
      vehicle: vehicle,
      position: subscribeToVehicleResponse,
      payload: Traffic | Status
    ) => void,
    getVehicle: (vehicle: vehicle) => vehicle | null
  ) => {
    const client = this.getConnection()
    if (!client) {
      console.warn('AppSync client not connected')
      return { unsubscribe: () => {} }
    }

    const subscription = client
      .graphql({
        query: subscribeToVehicleSubscription,
        variables: {
          serial_no: vehicle.device_id,
        },
      })
      .subscribe({
        next: (result: {
          data: SubscribeToVehicleSubscriptionSubscription
        }) => {
          const provider: VehicleSubscriptionsSubscriptionEvent = {
            data: result.data,
          }
          const topic =
            provider?.data?.subscribeToVehicleSubscription?.topic ?? ''
          const payload = JSON.parse(
            provider?.data?.subscribeToVehicleSubscription?.payload ?? '{}'
          )
          const time = provider?.data?.subscribeToVehicleSubscription?.tm ?? 0
          const target = getVehicle(vehicle) ?? vehicle
          switch (true) {
            case /traffic/.test(topic):
              this.trafficAction(payload, target, time, trafficAction)
              break
            case /status/.test(topic):
              this.statusAction(payload, target, time, statusAction)
              break
            case /sensor/.test(topic):
              this.sensorAction(payload, time, target)
              break
          }
        },
        error: (err: unknown) => console.warn('Subscription error:', err),
      })

    return subscription
  }
}

export default AppSyncProvider
